
import React, { useEffect, useState, useRef } from 'react';
import { useArticleEditor } from '@/hooks/useArticleEditor';
import { useParams } from 'react-router-dom';
import EditorHeader from './editor/EditorHeader';
import BlockEditor, { BlockEditorRef } from './editor/BlockEditor';
import BlockInsertionPanel from './editor/BlockInsertionPanel';
import ArticleSettings from './article-settings/ArticleSettings';
import { Loader2 } from 'lucide-react';
import { 
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { AiAssistantProvider } from '@/contexts/ai-assistant/AIAssistantContext';
import LeadField from './editor/parts/LeadField';
import HeaderImageUpload from './editor/parts/HeaderImageUpload';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';

const Editor = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const {
    title,
    setTitle,
    lead,
    setLead,
    content,
    setContent,
    editorData,
    setEditorData,
    isSaving,
    isPublishing,
    lastSaved,
    saveArticle,
    publishArticle,
    metadata,
    updateMetadata,
    isNew,
    isLoading
  } = useArticleEditor();
  
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const blockEditorRef = useRef<BlockEditorRef>(null);

  useEffect(() => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You need to be logged in to create or edit articles.",
        variant: "destructive"
      });
      navigate('/auth');
    }
  }, [user, navigate, toast]);

  useEffect(() => {
    if (content && (!editorData || !editorData.blocks || editorData.blocks.length === 0)) {
      const initialData = {
        version: "1.0",
        blocks: [
          {
            type: "paragraph",
            data: {
              text: content
            }
          }
        ]
      };
      setEditorData(initialData);
    }
  }, [content, editorData]);

  const handleSave = async (data: any) => {
    console.log("handleSave called with data:", data);
    
    setEditorData(data);
    
    const plainText = extractPlainTextFromBlocks(data.blocks);
    setContent(plainText);
    
    const result = await saveArticle();
    console.log("Save result:", result);
    return result;
  };

  const extractPlainTextFromBlocks = (blocks: any[]) => {
    if (!blocks || !blocks.length) return '';
    
    return blocks.map(block => {
      switch (block.type) {
        case 'paragraph':
          return block.data.text?.replace(/<[^>]*>/g, '') || '';
        case 'header':
          return block.data.text || '';
        case 'list':
          return (block.data.items || []).join('\n');
        case 'quote':
          return `${block.data.text || ''}\n- ${block.data.caption || ''}`;
        case 'warning':
          return `${block.data.title || ''}\n${block.data.message || ''}`;
        case 'image':
          return block.data.caption || '';
        default:
          return '';
      }
    }).join('\n\n');
  };

  const handleOnSave = async (editorData?: any) => {
    console.log("handleOnSave called, id:", id, "isNew:", isNew);
    
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You need to be logged in to save articles.",
        variant: "destructive"
      });
      return false;
    }
    
    if (editorData) {
      setEditorData(editorData);
      const plainText = extractPlainTextFromBlocks(editorData.blocks);
      setContent(plainText);
    }
    
    const result = await saveArticle();
    console.log("Save article result:", result);
    return result;
  };

  const handleOnPublish = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You need to be logged in to publish articles.",
        variant: "destructive"
      });
      return Promise.resolve();
    }
    
    await publishArticle();
    return Promise.resolve();
  };

  const handleAddBlock = () => {
    if (blockEditorRef.current) {
      blockEditorRef.current.insertBlock();
    }
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    
    if (!destination) {
      return;
    }
    
    if (destination.droppableId === 'editor-drop-area') {
      const draggableId = result.draggableId;
      const blockTypeMatch = draggableId.match(/block-(.*?)-\d+/);
      
      if (blockTypeMatch && blockTypeMatch[1]) {
        const blockType = blockTypeMatch[1];
        console.log(`Inserting block of type: ${blockType} via drag and drop`);
        
        handleAddBlock();
        
        toast({
          title: "Block Added",
          description: `Added a new ${blockType} block to your article`,
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AiAssistantProvider>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="space-y-6 max-w-full pb-10">
          <EditorHeader
            title={title}
            setTitle={setTitle}
            lastSaved={lastSaved}
            isNew={isNew}
            onSave={handleOnSave}
            onPublish={handleOnPublish}
            isSaving={isSaving}
            isPublishing={isPublishing}
            content={content}
            lead={lead}
            headerImageUrl={metadata.headerImage}
          />
          
          <div className="space-y-6 container mx-auto px-4 max-w-7xl">
            <HeaderImageUpload 
              headerImageUrl={metadata.headerImage} 
              onHeaderImageChange={(url) => updateMetadata('headerImage', url)} 
            />
            
            <LeadField 
              lead={lead} 
              setLead={setLead} 
              content={content}
            />
            
            <ResizablePanelGroup 
              direction="horizontal" 
              className="min-h-[calc(100vh-350px)] bg-background rounded-lg shadow-sm border border-border/40"
            >
              <ResizablePanel 
                defaultSize={18} 
                minSize={15}
                maxSize={25}
                className={cn(
                  "transition-all duration-300",
                  leftPanelCollapsed ? "w-0 !min-w-0" : ""
                )}
                collapsible={true}
                collapsedSize={0}
                onCollapse={() => setLeftPanelCollapsed(true)}
                onExpand={() => setLeftPanelCollapsed(false)}
              >
                <BlockInsertionPanel 
                  onAddBlock={handleAddBlock} 
                  droppableId="left-sidebar-blocks"
                />
              </ResizablePanel>
              
              <ResizableHandle withHandle className="bg-border/30" />
              
              <ResizablePanel defaultSize={leftPanelCollapsed || rightPanelCollapsed ? 85 : 62}>
                <div className="h-full overflow-y-auto p-4 lg:p-6 bg-white dark:bg-gray-950 rounded-md">
                  <BlockEditor
                    ref={blockEditorRef}
                    initialData={editorData}
                    onSave={handleSave}
                    isSaving={isSaving}
                    isPublishing={isPublishing}
                    articleId={id}
                    onAddBlock={handleAddBlock}
                    droppableId="editor-drop-area"
                  />
                </div>
              </ResizablePanel>
              
              <ResizableHandle withHandle className="bg-border/30" />
              
              <ResizablePanel 
                defaultSize={20} 
                minSize={15}
                maxSize={30}
                className={cn(
                  "transition-all duration-300",
                  rightPanelCollapsed ? "w-0 !min-w-0" : ""
                )}
                collapsible={true}
                collapsedSize={0}
                onCollapse={() => setRightPanelCollapsed(true)}
                onExpand={() => setRightPanelCollapsed(false)}
              >
                <ArticleSettings 
                  metadata={metadata} 
                  updateMetadata={updateMetadata}
                  onSave={handleOnSave}
                  content={content}
                  title={title}
                />
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </div>
      </DragDropContext>
    </AiAssistantProvider>
  );
};

export default Editor;
